/*
Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.

Licensed under the Apache License, Version 2.0 (the "License").
You may not use this file except in compliance with the License.
You may obtain a copy of the License at

    http://www.apache.org/licenses/LICENSE-2.0

Unless required by applicable law or agreed to in writing, software
distributed under the License is distributed on an "AS IS" BASIS,
WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
See the License for the specific language governing permissions and
limitations under the License.
*/
import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Link,
  LinearProgress,
  IconButton,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import ExtractionIcon from '@mui/icons-material/ManageSearch';
import { LoadingButton } from '@mui/lab';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  handleDownload,
  refreshGlossaryExtractionStatus,
  GlossaryExtractionState,
  languageOptions,
} from '@ink-ai/portal/reducers/glossary-extraction';
import { useDispatch } from 'react-redux';
import { GlossaryExtractionUploadUrlDtoLanguageEnum } from '@ink-ai/insight-service-sdk';

interface FileUploadAreaProps {
  glossaryExtraction: GlossaryExtractionState;
  language: GlossaryExtractionUploadUrlDtoLanguageEnum;
  handleDrop: (event: React.DragEvent<HTMLDivElement>) => void;
  handleDragOver: (event: React.DragEvent<HTMLDivElement>) => void;
  handleFileChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleRemoveFile: () => void;
  formatFileSize: (size: number) => string;
}

export const FileUploadArea: React.FC<FileUploadAreaProps> = ({
  glossaryExtraction,
  language,
  handleDrop,
  handleDragOver,
  handleFileChange,
  handleRemoveFile,
  formatFileSize,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch<any>();
  
  return (
    <Box
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        border: '2px dashed #ccc',
        borderRadius: '4px',
        padding: '20px',
        textAlign: 'center',
        width: '100%',
        maxWidth: '100%',
        boxSizing: 'border-box',
        height: '200px',
        margin: '2rem 0',
        overflow: 'hidden',
      }}
    >
      <input
        accept=".docx, .pdf"
        className="hidden"
        id={`upload-file-${language}`}
        type="file"
        onChange={handleFileChange}
      />
      {!glossaryExtraction.fileNames[language] ? (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
            <UploadFileIcon sx={{ fontSize: '24px', color: '#2196F3' }} />
          </Box>
          <Typography variant="body2" color="text.primary">
          {languageOptions.get(language)}
          </Typography>
          <label htmlFor={`upload-file-${language}`} style={{ cursor: 'pointer' }}>
            <Link component="span" underline="hover">
              {t('glossaryExtraction.document.clickToUpload')}
            </Link>
          </label>
          &nbsp;{t('glossaryExtraction.document.dragAndDrop')}
          <Typography variant="body2" color="text.secondary">
            {t('glossaryExtraction.document.fileFormat')}
          </Typography>
        </Box>
      ) : (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            alignItems: 'center',
            mt: 1,
            backgroundColor: '#f5f5f5',
            padding: '10px',
            borderRadius: '4px',
            width: '100%',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
            <InsertDriveFileIcon sx={{ mr: 3, color: '#2196F3' }} />
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                flexGrow: 1,
              }}
            >
              <Typography variant="body2">{glossaryExtraction.fileNames[language]}</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  {formatFileSize(glossaryExtraction.fileSizes[language])}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ ml: 1 }}
                >
                  {glossaryExtraction.glossaryExtractionStatus.charAt(0).toUpperCase() +
                    glossaryExtraction.glossaryExtractionStatus.slice(1)}
                </Typography>
              </Box>
            </Box>
            {glossaryExtraction.glossaryExtractionStatus === 'extracting' && (
              <IconButton
                onClick={() => dispatch(refreshGlossaryExtractionStatus())}
                sx={{ ml: 2 }}
              >
                <RefreshIcon />
              </IconButton>
            )}
            <IconButton onClick={handleRemoveFile} sx={{ ml: 2 }}>
              <DeleteIcon />
            </IconButton>
          </Box>
          {glossaryExtraction.glossaryExtractionStatus === 'uploading' && (
            <Box sx={{ width: '80%', mt: 2 }}>
              <LinearProgress
                variant={glossaryExtraction.progress !== undefined ? "determinate" : "indeterminate"}
                value={glossaryExtraction.progress || 0}
              />
            </Box>
          )}
          {(glossaryExtraction.glossaryExtractionStatus === 'extracting' ||
          glossaryExtraction.glossaryExtractionStatus === 'queueing') && (
            <Box sx={{ width: '80%', mt: 2 }}>
              <LinearProgress variant="indeterminate" />
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

interface GlossaryExtractionControlProps {
  glossaryExtraction: GlossaryExtractionState;
  handleExtractGlossary: () => void;
  handleDownload: (uuid: string) => void;
}

const GlossaryExtractionControl: React.FC<GlossaryExtractionControlProps> = ({
  glossaryExtraction,
  handleExtractGlossary,
  handleDownload,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch<any>();
  const allLanguagesHaveFiles = glossaryExtraction.languages.every(
    (lang) => glossaryExtraction.fileNames[lang]
  ) && glossaryExtraction.languages.length >= 2;
  
  return (
    <LoadingButton
      variant="contained"
      loadingPosition="start"
      color="primary"
      sx={{
        alignSelf: 'flex-end',
        mt: 2,
        textTransform: 'none',
        ...(allLanguagesHaveFiles
          ? {} // Active button style (contained)
          : {
              backgroundColor: '#f5f5f5', // Disabled background
              color: '#9e9e9e',           // Disabled text color
              cursor: 'not-allowed',      // Disabled cursor
            }),
      }}      
      disabled={!allLanguagesHaveFiles} // Disable button if not all languages have files
      onClick={
        glossaryExtraction.glossaryExtractionStatus === 'completed'
          ? () => handleDownload(glossaryExtraction.uuid)
          : handleExtractGlossary
      }
      loading={glossaryExtraction.loading}
      startIcon={
        glossaryExtraction.glossaryExtractionStatus === 'completed' ? (
          <DownloadIcon />
        ) : glossaryExtraction.glossaryExtractionStatus === 'waiting upload' ? undefined : (
          <ExtractionIcon />
        )
      }
    >
      {glossaryExtraction.glossaryExtractionStatus === 'completed'
        ? t('glossaryExtraction.document.download')
        : glossaryExtraction.glossaryExtractionStatus === 'waiting upload'
        ? t('glossaryExtraction.document.extractGlossary')
        : glossaryExtraction.glossaryExtractionStatus === 'queueing'
          ? t('glossaryExtraction.document.queueing')
          : t('glossaryExtraction.document.extracting')}
    </LoadingButton>
  );
};

export const GlossaryExtractionArea: React.FC<GlossaryExtractionControlProps> = ({
  glossaryExtraction,
  handleExtractGlossary,
  handleDownload,
}) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
    <GlossaryExtractionControl
      glossaryExtraction={glossaryExtraction}
      handleExtractGlossary={handleExtractGlossary}
      handleDownload={handleDownload}
    />
  </Box>
);
